/**
 * **Quem** registrou um preço.
 *
 * ## Não é `source`, e a diferença importa
 *
 * `PricePoint.source` já responde *de onde veio o preço* — comprado, visto na
 * gôndola, lido da Nota Paraná. Esta é outra pergunta: **quem pôs isso aqui**. A
 * mesma compra pode ser registrada pelo Murilo ou por um agente que garimpou o
 * preço num site, e as duas coisas merecem confiança diferente.
 *
 * ## Três tipos, e o vocabulário é o de `Comment`
 *
 * `Comment.authorType` já resolveu esse problema com `'user' | 'system' | 'ai'`.
 * Aqui são `'user' | 'ai' | 'other'`:
 *
 * - **`user`** — um membro da casa. `id` é o uid, e o **adapter** o carimba a
 *   partir de quem está logado: deixá-lo à tela permitiria registrar um preço em
 *   nome da outra pessoa, que é exatamente o que `isOwnUserComment` já impede
 *   nos comentários.
 * - **`ai`** — um agente. *"Quando um agente de IA tiver selecionado e tiver
 *   encontrado alguma coisa, ele diga que foi ele que encontrou aquilo."* É o
 *   tipo que existe para o preço garimpado ter etiqueta.
 * - **`other`** — alguém que não é usuário do sistema. *"Não precisa ser
 *   necessariamente um usuário dentro do sistema."* A sogra que viu na feira.
 *
 * ## `label` é gravado, e não resolvido na leitura
 *
 * O nome fica guardado junto do ponto, e não é buscado pelo uid a cada
 * renderização. É denormalização deliberada: o histórico de preço é um
 * **registro histórico**, e "quem registrou" é o que era verdade naquele dia. Um
 * membro que troque o nome não reescreve o passado — e a tela não precisa de uma
 * leitura de membros para desenhar uma lista de preços.
 */

export type RecordedByKind = 'user' | 'ai' | 'other'

export type RecordedBy = {
  kind: RecordedByKind
  /** O uid, e **só** quando `kind` é `'user'`. `null` nos outros dois. */
  id: string | null
  /** O nome mostrado. Ver o cabeçalho para por que ele é gravado. */
  label: string
}

/** O que a tela oferece quando ninguém escolheu nada. */
export const RECORDED_BY_PADRAO: RecordedByKind = 'user'

/**
 * O rótulo final, já resolvido.
 *
 * Nome em branco vira um genérico por tipo em vez de string vazia: um "por" sem
 * ninguém depois é pior do que "por um agente".
 */
export function labelDe(kind: RecordedByKind, escrito: string, doUsuario: string | null): string {
  const limpo = escrito.trim()
  if (limpo !== '') return limpo
  if (kind === 'user') return doUsuario?.trim() ?? 'alguém da casa'
  return kind === 'ai' ? 'um agente' : 'alguém'
}
